/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import { actions } from "./actions";
import { reducerLogger } from "../../../../utility/reducerLogger";

const newContact = () => ({
	contactId: null,
	fullName: "",
	phone: "",
	tags: [],
	azureAdUserId: "",
});

const isSearchMatch = ({ fullName, phone, tags }, lowerCasedSearchTerm) => {
	if (fullName && fullName.toLowerCase().includes(lowerCasedSearchTerm)) {
		return true;
	}

	if (phone && phone.includes(lowerCasedSearchTerm)) {
		return true;
	}

	if (tags && tags.some((t) => t.toLowerCase().includes(lowerCasedSearchTerm))) {
		return true;
	}

	return false;
};

const deleteContactsLabels = (selected, total) => {
	if (selected === total) {
		return {
			title: `Delete All Contacts?`,
			message: `Are you sure you want to delete all contacts?`,
		};
	}

	if (selected === 1) {
		return {
			title: `Delete Contact?`,
			message: `Are you sure you want to delete the contact?`,
		};
	}

	return {
		title: `Delete ${selected} Contacts?`,
		message: `Are you sure you want to delete ${selected} contacts?`,
	};
};

const filterContacts = (searchTerm, contacts) => {
	const lowerCasedSearchTerm = searchTerm?.toLowerCase() || "";

	return lowerCasedSearchTerm
		? contacts.map((c) => ({ ...c, isFiltered: isSearchMatch(c, lowerCasedSearchTerm) }))
		: contacts.map((c) => ({ ...c, isFiltered: true }));
};

export const reducer = reducerLogger((state, action) => {
	switch (action.type) {
		case actions.START_EDIT_CONTACT:
			const contactToEdit = state.contacts.find(({ contactId }) => contactId === action.payload.contactId);
			return { ...state, contactToEdit: { ...contactToEdit } };
		case actions.START_NEW_CONTACT:
			return { ...state, contactToEdit: { ...newContact() } };
		case actions.CANCEL_EDIT_CONTACT:
			return { ...state, contactToEdit: {} };
		case actions.FETCH_CONTACTS_STARTED:
			return {
				...state,
				contactsLoading: true,
			};
		case actions.FETCH_CONTACTS_COMPLETED:
			return {
				...state,
				contacts: filterContacts(state.currentSearchTerm, action.payload.contacts),
				contactsLoading: false,
			};
		case actions.FETCH_CONTACTS_ERROR:
			return {
				...state,
				contacts: null,
				contactsLoading: false,
			};
		case actions.DELETED_CONTACTS:
			return {
				...state,
				contacts: state.contacts.filter((c) => !action.payload.contactIdsToDelete.includes(c.contactId)),
				deleteModal: {
					title: "",
					message: "",
					onClose: () => {},
					confirmOpen: false,
				},
			};
		case actions.SHOW_DELETE_CONFIRMATION:
			const { title, message } = deleteContactsLabels(action.payload.deleteCount, state.contacts.length);
			return {
				...state,
				deleteModal: {
					title,
					message,
					onClose: async (shouldDelete) =>
						shouldDelete ? await action.payload.onConfirm() : await action.payload.onCancel(),
					confirmOpen: true,
				},
			};
		case actions.CANCEL_DELETE_CONFIRMATION:
			return {
				...state,
				deleteModal: {
					title: "",
					message: "",
					onClose: () => {},
					confirmOpen: false,
				},
			};
		case actions.FILTER_CONTACTS:
			return {
				...state,
				currentSearchTerm: action.payload.searchTerm,
				contacts: filterContacts(action.payload.searchTerm, state.contacts),
			};
		case actions.TOGGLE_SELECT_CONTACT:
			return {
				...state,
				contacts: state.contacts.map((c) => {
					if (c.contactId === action.payload.contactId) {
						return { ...c, isSelected: !c.isSelected };
					}
					return c;
				}),
			};
		case actions.TOGGLE_SELECT_ALL_CONTACTS:
			const filteredContacts = action.payload.filteredContacts;
			const allContactsSelected =
				filteredContacts.filter(({ isSelected }) => isSelected).length === filteredContacts.length;
			const filteredContactIds = filteredContacts.map((c) => c.contactId);
			return {
				...state,
				contacts: state.contacts.map((c) => {
					if (filteredContactIds.includes(c.contactId)) {
						c.isSelected = !allContactsSelected;
					}
					return c;
				}),
			};
		case actions.SAVE_CONTACTS:
			let newContacts = [];

			if (!state.contactToEdit.contactId) {
				newContacts = [{ ...action.payload.contact }, ...state.contacts];
			} else {
				newContacts = state.contacts.map((c) => {
					if (c.contactId === action.payload.contact.contactId) {
						return { ...c, ...action.payload.contact };
					}
					return c;
				});
			}
			return {
				...state,
				contacts: filterContacts(state.currentSearchTerm, newContacts),
				contactToEdit: {},
			};
		case actions.CREATE_CONTACTS:
			const combinedContacts = [...action.payload.contacts.map((c) => ({ ...c })), ...state.contacts];
			return {
				...state,
				contacts: filterContacts(state.currentSearchTerm, combinedContacts),
			};
		case actions.FETCH_AZURE_USERS_STARTED:
			return {
				...state,
				azureAdGroups: [],
				azureAdUsers: [],
				azureAdUsersLoading: true,
				azureAdLoadError: false,
			};
		case actions.FETCH_AZURE_USERS_COMPLETED:
			return {
				...state,
				azureAdUsers: action.payload.groupMembers,
				azureAdGroups: action.payload.groupNames,
				azureAdUsersLoading: false,
				azureAdLoadError: false,
			};
		case actions.FETCH_AZURE_USERS_ERROR:
			return {
				...state,
				azureAdUsers: [],
				azureAdGroups: [],
				azureAdUsersLoading: false,
				azureAdLoadError: action.payload.message,
			};
		case actions.FETCH_AZURE_CONFIG_STARTED:
			return {
				...state,
				azureConfig: {},
				isAzureConfigLoading: true,
			};
		case actions.FETCH_AZURE_CONFIG_COMPLETED:
			return {
				...state,
				isAzureConfigLoading: false,
				azureConfig: action.payload.TeamsSyncConfiguration || {},
			};
		default:
			return state;
	}
});
